import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API_BASE = `${BACKEND_URL}/api`;

// Create axios instance
const api = axios.create({
  baseURL: API_BASE,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 30000, // 30 seconds — prevents infinite stuck "Processing..." on slow/hanging requests
});

// Add auth token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle auth errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      // Signal React auth context to clear state — ProtectedRoute handles redirect
      window.dispatchEvent(new Event('auth-logout'));
    }
    // Demo tenant write-block (HTTP 423) — show a single friendly toast.
    // Throttled to avoid spamming when multiple mutations fire on one click.
    if (error.response?.status === 423 && error.response?.data?.demo_locked) {
      try {
        // Lazy-load toast so the interceptor stays cheap.
        const now = Date.now();
        if (!window.__demoLockToastAt || now - window.__demoLockToastAt > 2500) {
          window.__demoLockToastAt = now;
          import('sonner').then(({ toast }) => {
            toast.info(error.response.data.detail || 'Demo environment is view-only.', {
              description: 'Sign in with your own account to make changes.',
              duration: 4000,
            });
          }).catch(() => {});
        }
      } catch (_) { /* silent */ }
    }
    // Surface timeout as a readable message instead of raw axios error
    if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
      error.userMessage = 'Request timed out. Please check your connection and try again.';
    }
    return Promise.reject(error);
  }
);

// Auth APIs
export const authAPI = {
  register: (data) => api.post('/auth/register', data),
  login: (data) => api.post('/auth/login', data),
  pinLogin: (data) => api.post('/auth/pin-login', data),
  getMe: () => api.get('/auth/me'),
  refreshToken: () => api.post('/auth/refresh-token'),
  forgotPassword: (data) => api.post('/auth/forgot-password', data),
  verifyResetToken: (token) => api.get(`/auth/verify-reset-token?token=${token}`),
  resetPassword: (data) => api.post('/auth/reset-password', data),
  logoutUser: () => api.post('/auth/logout'),
  googleAuth: (data) => api.post('/auth/google', data),
  changePassword: (data) => api.post('/auth/change-password', data),
};

// Restaurant Group APIs
export const restaurantGroupAPI = {
  create: (data) => api.post('/restaurant-groups', data),
  getMine: () => api.get('/restaurant-groups/mine'),
  updateSettings: (data) => api.put('/restaurant-groups/mine/settings', data),
};

// Restaurant APIs
export const restaurantAPI = {
  create: (data) => api.post('/restaurants', data),
  getAll: () => api.get('/restaurants'),
  getOne: (id) => api.get(`/restaurants/${id}`),
  update: (id, data) => api.put(`/restaurants/${id}`, data),
  uploadLogo: (id, file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post(`/restaurants/${id}/logo`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  deleteLogo: (id) => api.delete(`/restaurants/${id}/logo`),
  uploadIcon: (id, file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post(`/restaurants/${id}/icon`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  deleteIcon: (id) => api.delete(`/restaurants/${id}/icon`),
  delete: (id, pin) => api.delete(`/restaurants/${id}`, { params: { pin } }),
};

// Staff APIs
export const staffAPI = {
  create: (restaurantId, data) => api.post(`/restaurants/${restaurantId}/staff`, data),
  getAll: (restaurantId) => api.get(`/restaurants/${restaurantId}/staff`),
  getOne: (staffId) => api.get(`/staff/${staffId}`),
  update: (staffId, data) => api.put(`/staff/${staffId}`, data),
  delete: (staffId) => api.delete(`/staff/${staffId}`),
  resetPin: (staffId) => api.post(`/staff/${staffId}/reset-pin`),
  // Staff location assignments
  getWithLocations: () => api.get('/staff-with-locations'),
  getLocations: (staffId) => api.get(`/staff/${staffId}/locations`),
  assignLocation: (staffId, data) => api.post(`/staff/${staffId}/locations`, data),
  updateLocation: (staffId, locationId, params) => api.put(`/staff/${staffId}/locations/${locationId}`, null, { params }),
  removeLocation: (staffId, locationId) => api.delete(`/staff/${staffId}/locations/${locationId}`),
};

// Time Clock APIs
export const timeClockAPI = {
  clockIn: () => api.post('/time-clock/clock-in'),
  clockOut: () => api.post('/time-clock/clock-out'),
  getShifts: (params) => api.get('/shifts', { params }),
  getActiveShift: () => api.get('/shifts/active'),
  approveShift: (shiftId) => api.put(`/shifts/${shiftId}/approve`),
  rejectShift: (shiftId, data) => api.put(`/shifts/${shiftId}/reject`, data),
  editShift: (shiftId, data) => api.put(`/shifts/${shiftId}/edit`, data),
};

// Schedule APIs
export const scheduleAPI = {
  create: (restaurantId, data) => api.post(`/restaurants/${restaurantId}/schedules`, data),
  getAll: (restaurantId, params) => api.get(`/restaurants/${restaurantId}/schedules`, { params }),
  delete: (scheduleId) => api.delete(`/schedules/${scheduleId}`),
  setAvailability: (data) => api.post('/availability', data),
  getAvailability: (userId) => api.get(`/availability/${userId}`),
  // Publishing
  publish:    (restaurantId, data) => api.post(`/restaurants/${restaurantId}/schedules/publish`, data),
  unpublish:  (restaurantId, data) => api.post(`/restaurants/${restaurantId}/schedules/unpublish`, data),
  getDraftCount: (restaurantId, weekStart) => api.get(`/restaurants/${restaurantId}/schedules/draft-count`, { params: { week_start: weekStart } }),
  // Bulk creation
  createBulk: (restaurantId, data) => api.post(`/restaurants/${restaurantId}/schedules/bulk`, data),
};

// Job Roles APIs
export const jobRolesAPI = {
  getAll: () => api.get('/job-roles'),
  create: (data) => api.post('/job-roles', data),
  update: (roleId, data) => api.put(`/job-roles/${roleId}`, data),
  delete: (roleId) => api.delete(`/job-roles/${roleId}`),
};

// Shift Templates APIs
export const shiftTemplatesAPI = {
  getAll: () => api.get('/shift-templates'),
  create: (data) => api.post('/shift-templates', data),
  update: (templateId, data) => api.put(`/shift-templates/${templateId}`, data),
  delete: (templateId) => api.delete(`/shift-templates/${templateId}`),
};

// Task APIs
export const taskAPI = {
  create: (restaurantId, data) => api.post(`/restaurants/${restaurantId}/tasks`, data),
  getAll: (restaurantId, params) => api.get(`/restaurants/${restaurantId}/tasks`, { params }),
  update: (taskId, data) => api.put(`/tasks/${taskId}`, data),
  delete: (taskId) => api.delete(`/tasks/${taskId}`),
  reset: (restaurantId) => api.post(`/restaurants/${restaurantId}/tasks/reset`),
};

// Announcement APIs
export const announcementAPI = {
  create: (restaurantId, data) => api.post(`/restaurants/${restaurantId}/announcements`, data),
  getAll: (restaurantId) => api.get(`/restaurants/${restaurantId}/announcements`),
  delete: (announcementId) => api.delete(`/announcements/${announcementId}`),
};

// Document APIs
export const documentAPI = {
  upload: (restaurantId, file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post(`/restaurants/${restaurantId}/documents`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  getAll: (restaurantId) => api.get(`/restaurants/${restaurantId}/documents`),
  acknowledge: (docId) => api.post(`/documents/${docId}/acknowledge`),
  delete: (docId) => api.delete(`/documents/${docId}`),
  getDownloadUrl: (docId) => api.get(`/documents/${docId}/download`),
};

// Report APIs
export const reportAPI = {
  getHours: (restaurantId, startDate, endDate) =>
    api.get('/reports/hours', { params: { restaurant_id: restaurantId, start_date: startDate, end_date: endDate } }),
  getWeeklySummary: (restaurantId, weekStart) =>
    api.get('/reports/weekly-summary', { params: { restaurant_id: restaurantId, week_start: weekStart } }),
  exportCSV: (restaurantId, startDate, endDate) =>
    api.get(`/reports/export-csv?restaurant_id=${restaurantId}&start_date=${startDate}&end_date=${endDate}`, {
      responseType: 'blob'
    }),
};

// Executive Dashboard API
export const executiveAPI = {
  getDashboard: (startDate, endDate, restaurantId = null) =>
    api.get('/executive/dashboard', { params: { start_date: startDate, end_date: endDate, ...(restaurantId && restaurantId !== 'all' ? { restaurant_id: restaurantId } : {}) } }),
};

// Stripe APIs
export const stripeAPI = {
  createCheckout: (data) => api.post('/stripe/create-checkout', { ...data, origin_url: window.location.origin }),
  getCheckoutStatus: (sessionId) => api.get(`/stripe/checkout-status/${sessionId}`),
  createSetupIntent: (data) => api.post('/stripe/create-setup-intent', data),
  createSubscription: (data) => api.post('/stripe/create-subscription', data),
  validateCoupon: (data) => api.post('/validate-coupon', data),
  createFreeSubscription: (data) => api.post('/stripe/create-free-subscription', data),
  getSubscriptionStatus: () => api.get('/stripe/subscription-status'),
  upgradePlan: (data) => api.post('/stripe/upgrade-plan', data),
  changeBillingCycle: (billingCycle) => api.post(`/stripe/change-billing-cycle?billing_cycle=${billingCycle}`),
  cancelSubscription: () => api.post('/stripe/cancel-subscription'),
  resumeSubscription: () => api.post('/stripe/resume-subscription'),
  createBillingPortalSession: (returnUrl) =>
    api.post('/billing/portal-session', { return_url: returnUrl || window.location.href }),
  getInvoices: () => api.get('/billing/invoices'),
};

// Pricing API - Public endpoint (no auth required)
export const pricingAPI = {
  getPricing: () => axios.get(`${API_BASE}/pricing`),
  getPlanPricing: (planType) => axios.get(`${API_BASE}/pricing/${planType}`),
};

export const featureRequestAPI = {
  submit: (data) => api.post('/feature-requests', data),
};

export const contactSupportAPI = {
  submit: (data) => api.post('/contact-support', data),
};

// Super Admin APIs
export const superAdminAPI = {
  createAccount: (data) => api.post('/super-admin/create-account', data),
  getAccounts: () => api.get('/super-admin/accounts'),
  updateStatus: (groupId, status) => api.put(`/super-admin/accounts/${groupId}/status?status=${status}`),
  editAccount: (groupId, data) => api.put(`/super-admin/accounts/${groupId}`, data),
  updateOwnerRole: (groupId, role) => api.put(`/super-admin/accounts/${groupId}/owner-role`, { role }),
  deleteAccount: (groupId) => api.delete(`/super-admin/accounts/${groupId}`),
  impersonate: (userId) => api.post(`/super-admin/impersonate/${userId}`),
  createCoupon: (data) => api.post('/super-admin/coupons', data),
  getCoupons: () => api.get('/super-admin/coupons'),
  editCoupon: (couponId, data) => api.put(`/super-admin/coupons/${couponId}`, data),
  deleteCoupon: (couponId) => api.delete(`/super-admin/coupons/${couponId}`),
  toggleCoupon: (couponId) => api.put(`/super-admin/coupons/${couponId}/toggle`),
  backfillTenantDefaults: () => api.post('/super-admin/backfill-tenant-defaults'),
};

// Push Notification APIs
export const pushAPI = {
  subscribe: (subscription) => api.post('/push/subscribe', subscription),
  unsubscribe: () => api.delete('/push/unsubscribe'),
  getPending: () => api.get('/push/pending'),
};

// Notification APIs
export const notificationAPI = {
  getAll: () => api.get('/notifications'),
  markRead: (notificationId) => api.put(`/notifications/${notificationId}/read`),
  markAllRead: () => api.put('/notifications/read-all'),
};

// Support Ticket APIs (additive — staff-friendly ticket system)
export const supportTicketsAPI = {
  create: (data) => api.post('/support-tickets', data),
  list: () => api.get('/support-tickets'),
  listTenant: () => api.get('/support-tickets/tenant'),
  reply: (id, message) => api.post(`/support-tickets/${id}/reply`, { message }),
  updateStatus: (id, status) => api.patch(`/support-tickets/${id}/status`, { status }),
  markRepliesRead: () => api.post('/support-tickets/mark-replies-read'),
};

// Time-correction (missed punch) request APIs — additive
export const timeCorrectionsAPI = {
  create:   (data) => api.post('/timesheet-corrections', data),
  list:     (scope = 'mine') => api.get(`/timesheet-corrections?scope=${scope}`),
  approve:  (id, data = {}) => api.post(`/timesheet-corrections/${id}/approve`, data),
  reject:   (id, data = {}) => api.post(`/timesheet-corrections/${id}/reject`, data),
};

// Staff Availability APIs
export const availabilityAPI = {
  set: (data) => api.post('/staff/availability', data),
  get: (userId) => api.get(`/staff/availability/${userId}`),
  getAllForRestaurant: (restaurantId) => api.get(`/restaurants/${restaurantId}/staff-availability`),
};

// Quick Clock APIs (no auth required)
export const quickClockAPI = {
  getRestaurants: () => axios.get(`${API_BASE}/quick-clock/restaurants`),
  login: (data) => axios.post(`${API_BASE}/quick-clock/login`, data),
  action: (data) => axios.post(`${API_BASE}/quick-clock/action`, data),
};

// Timesheet APIs
export const timesheetAPI = {
  getEntries: (params) => api.get('/timesheets', { params }),
  getSummary: (params) => api.get('/timesheets/summary', { params }),
  getPayrollSummary: (params) => api.get('/timesheets/payroll-summary', { params }),
  payrollPreview: (data) => api.post('/timesheets/payroll-preview', data),
  exportCSV: (params) =>
    api.get('/timesheets/export/csv', { params, responseType: 'blob' }),
  exportRaw: (params) =>
    api.get('/timesheets/export', { params, responseType: 'blob' }),
  exportFormatted: (params) =>
    api.get('/timesheets/export/formatted', { params, responseType: 'blob' }),
  getLaborInsights: (params) =>
    api.get('/labor-insights', { params }),
  updateShiftKpi: (shiftId, data) =>
    api.put(`/timesheets/${shiftId}/kpi`, data),
  exportExcel: (params) =>
    api.get('/timesheets/export/excel', { params, responseType: 'blob' }),
  exportPayrollCSV: (params) =>
    api.get('/timesheets/export/payroll/csv', { params, responseType: 'blob' }),
  exportPayrollExcel: (params) =>
    api.get('/timesheets/export/payroll/excel', { params, responseType: 'blob' }),
  bulkApprove: (data) => api.post('/timesheets/bulk-approve', data),
  createManualEntry: (data) => api.post('/timesheets/manual-entry', data),
};

// Employee Self-Service APIs
export const employeeAPI = {
  getProfile: () => api.get('/employee/profile'),
  updateProfile: (data) => api.put('/employee/profile', data),
  getSchedule: (restaurantId, params) => api.get(`/restaurants/${restaurantId}/schedules`, { params }),
  getTimesheets: (params) => api.get('/timesheets', { params }),
  getAnnouncements: (restaurantId) => api.get(`/restaurants/${restaurantId}/announcements`),
  getDocuments: (restaurantId) => api.get(`/restaurants/${restaurantId}/documents`),
  // Onboarding checklist (additive)
  getOnboardingProgress: () => api.get('/employee/onboarding-progress'),
  markOnboardingEvent: (event) => api.post('/employee/onboarding-progress/event', { event }),
};

export const tenantAPI = {
  getSettings: () => api.get('/tenant/settings'),
};

export const aiAPI = {
  chat: (message, contextData) => api.post('/ai/chat', {
    message,
    ...(contextData ? { context_data: contextData } : {}),
  }),
  test: () => api.post('/ai/test'),
  saveSettings: (data) => api.put('/restaurant-groups/mine/settings', data),
};

// Shift Swap APIs
export const shiftSwapAPI = {
  create: (data) => api.post('/shift-swaps', data),
  getAll: (status) => api.get('/shift-swaps', { params: status ? { status } : {} }),
  getOne: (swapId) => api.get(`/shift-swaps/${swapId}`),
  respond: (swapId, response) => api.put(`/shift-swaps/${swapId}/respond?response=${response}`),
  cancel: (swapId) => api.delete(`/shift-swaps/${swapId}`),
};

// Admin Settings APIs (super_admin only) — served from billing module
export const adminSettingsAPI = {
  getPaymentConfig: () => api.get('/payment-config'),
  updateStripeConfig: (data) => api.put('/stripe-config', data),
  getStripeAuditLog: () => api.get('/stripe-audit-log'),
  getEvaAuditLog: (params) => api.get('/eva/audit-log', { params }),
  updateXenditConfig: (data) => api.put('/xendit-config', data),
  setPaymentProcessor: (processor) => api.put('/payment-processor', { payment_processor: processor }),
};

// Xendit payment
export const xenditAPI = {
  createInvoice: (data) => api.post('/xendit/create-invoice', { ...data, origin_url: window.location.origin }),
};

export const payslipsAPI = {
  runPayroll:    (data)     => api.post('/payslips/run', data),
  finalizeRun:   (runId)    => api.post(`/payslips/finalize/${runId}`),
  listRuns:      ()         => api.get('/payslips/runs'),
  getRunDetail:  (runId)    => api.get(`/payslips/runs/${runId}`),
  getMyPayslips: ()         => api.get('/payslips/my'),
  getPayslip:    (id)       => api.get(`/payslips/${id}`),
  listPayRules:  ()         => api.get('/pay-rules'),
  createPayRule: (data)     => api.post('/pay-rules', data),
  updatePayRule: (id, data) => api.put(`/pay-rules/${id}`, data),
  deletePayRule: (id)       => api.delete(`/pay-rules/${id}`),
  resendFailed:  (runId)    => api.post(`/payslips/resend/${runId}`),
  deleteRun:     (runId)    => api.delete(`/payslips/runs/${runId}`),
  exportCSV:     (runId)    => api.get(`/payslips/export/${runId}`, { responseType: 'blob' }),
  exportSummary: (runId)    => api.get(`/payslips/export-summary/${runId}`, { responseType: 'blob' }),
  updateRevenue: (runId, revenue) => api.put(`/payslips/runs/${runId}/revenue`, { total_revenue: revenue }),
};

export const leaveAPI = {
  getLeaveTypes:         ()         => api.get('/leave-types'),
  getLeaveTypesExtended: ()         => api.get('/leave-types/extended'),
  seedLeaveTypes:        ()         => api.post('/leave-types/seed'),
  createLeaveType:       (data)     => api.post('/leave-types', data),
  updateLeaveType:       (id, data) => api.put(`/leave-types/${id}`, data),
  deleteLeaveType:       (id)       => api.delete(`/leave-types/${id}`),
  getLeaveBalances:      ()         => api.get('/leave-balances'),
  getMyLeaveBalances:    ()         => api.get('/leave-balances/my'),
  getLeaveRequests:      ()         => api.get('/leave-requests'),
  createLeaveRequest:    (data)     => api.post('/leave-requests', data),
  approveLeaveRequest:   (id)       => api.put(`/leave-requests/${id}/approve`),
  rejectLeaveRequest:    (id)       => api.put(`/leave-requests/${id}/reject`),
  cancelLeaveRequest:    (id, data) => api.post(`/leave-requests/${id}/cancel`, data || { reason: null }),
  approvalQueue:         (params={})=> api.get('/leave-requests/approval-queue', { params }),
  calendar:              (params)   => api.get('/leave-requests/calendar', { params }),
  conflicts:             (params)   => api.get('/leave-requests/conflicts', { params }),
  audit:                 (id)       => api.get(`/leave-requests/${id}/audit`),
  listComments:          (id)       => api.get(`/leave-requests/${id}/comments`),
  addComment:            (id, text) => api.post(`/leave-requests/${id}/comments`, { text }),
  importTemplate:        (kind)     => api.get(`/leave-import/template/${kind}`, { responseType: 'blob' }),
  importValidate:        (kind, file) => {
    const fd = new FormData();
    fd.append('file', file);
    return api.post(`/leave-import/${kind}/validate`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
  importCommit:          (kind, file, partial=false) => {
    const fd = new FormData();
    fd.append('file', file);
    fd.append('partial', partial ? 'true' : 'false');
    return api.post(`/leave-import/${kind}/commit`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
};

// Absence Management — thin alias over leave endpoints.
// Backend is tenant-isolated via restaurant_group_id (see leave_routes.py).
export const absenceAPI = {
  list:    ()     => api.get('/leave-requests'),
  create:  (data) => api.post('/leave-requests', data),
  approve: (id)   => api.put(`/leave-requests/${id}/approve`),
  reject:  (id)   => api.put(`/leave-requests/${id}/reject`),
  types:   ()     => api.get('/leave-types'),
};

export const deductionAPI = {
  getDeductionTypes:     ()         => api.get('/deduction-types'),
  createDeductionType:   (data)     => api.post('/deduction-types', data),
  updateDeductionType:   (id, data) => api.put(`/deduction-types/${id}`, data),
  deleteDeductionType:   (id)       => api.delete(`/deduction-types/${id}`),
  getEmployeeDeductions: ()         => api.get('/employee-deductions'),
  assignDeduction:       (data)     => api.post('/employee-deductions', data),
  removeAssignment:      (id)       => api.delete(`/employee-deductions/${id}`),
};

export const incentiveAPI = {
  getPools:          ()     => api.get('/incentive-pools'),
  createPool:        (data) => api.post('/incentive-pools', data),
  distributePool:    (id)   => api.post(`/incentive-pools/${id}/distribute`),
  getDistributions:  (id)   => api.get(`/incentive-pools/${id}/distributions`),
  deletePool:        (id)   => api.delete(`/incentive-pools/${id}`),
};

export const sendpulseAPI = {
  getConfig:       ()         => api.get('/sendpulse/config'),
  updateConfig:    (data)     => api.put('/sendpulse/config', data),
  sendTestEmail:   (email)    => api.post('/sendpulse/test', { recipient_email: email }),
  getLogs:         (limit=50) => api.get(`/sendpulse/logs?limit=${limit}`),
  previewEmail:    (type)     => api.get(`/sendpulse/preview/${type}`),
};

export const resendAPI = {
  getConfig:       ()      => api.get('/resend/config'),
  updateConfig:    (data)  => api.put('/resend/config', data),
  sendTestEmail:   (email) => api.post('/resend/test', { recipient_email: email }),
};

export const holidayAPI = {
  list:                (params = {})           => api.get('/holidays', { params }),
  create:              (data)                  => api.post('/holidays', data),
  patch:               (id, data)              => api.patch(`/holidays/${id}`, data),
  remove:              (id)                    => api.delete(`/holidays/${id}`),
  resolve:             (dateIso, locationId)   => api.get('/holidays/resolve', {
                                                    params: locationId ? { date: dateIso, location_id: locationId } : { date: dateIso },
                                                  }),
  resolveBatch:        async (datesIso, locationId) => {
    // Lightweight client-side batch using single endpoint to avoid backend changes.
    // Returns map<dateIso, payload>.
    const results = await Promise.all(
      datesIso.map((d) =>
        api.get('/holidays/resolve', { params: locationId ? { date: d, location_id: locationId } : { date: d } })
          .then((r) => [d, r.data])
          .catch(() => [d, null])
      ),
    );
    return Object.fromEntries(results);
  },
  syncStatus:          ()                      => api.get('/holidays/sync/status'),
  syncDiagnostics:     ()                      => api.get('/holidays/sync/diagnostics'),
  syncLogs:            (params = {})           => api.get('/holidays/sync/logs', { params }),
  syncCountry:         (country, year)         => api.post(`/holidays/sync/${country}`, null, {
                                                    params: year ? { year } : {},
                                                  }),
  syncRunNow:          ()                      => api.post('/holidays/sync/run-now'),
  supportedCountries:  ()                      => api.get('/holidays/supported-countries'),
  providers:           ()                      => api.get('/holidays/providers'),
  phOverride:          (data)                  => api.post('/holidays/ph-override', data),
  // Phase 3
  resolverMetrics:     ()                      => api.get('/holidays/resolver-metrics'),
  clearCache:          ()                      => api.post('/holidays/cache/clear'),
  coverageHealth:      ()                      => api.get('/holidays/coverage-health'),
  upcomingImpact:      (params = {})           => api.get('/holidays/upcoming-impact', { params }),
  applyCountryYear:    (params)                => api.post('/holidays/apply-country-year', null, { params }),
  seedPacks:           ()                      => api.get('/holidays/seed-packs'),
  applySeedPack:       (key, params = {})      => api.post(`/holidays/seed-packs/${key}/apply`, null, { params }),
  phAuditLog:          (params = {})           => api.get('/holidays/ph-audit-log', { params }),
  featureFlags:        ()                      => api.get('/holidays/feature-flags'),
  setFeatureFlag:      (flag, data)            => api.put(`/holidays/feature-flags/${flag}`, data),
  featureFlagAudit:    ()                      => api.get('/holidays/feature-flags/audit'),
};

export const shiftReplacementAPI = {
  suggest:        (shiftId)              => api.post('/shift-replacements/suggest', { shift_id: shiftId }),
  create:         (data)                 => api.post('/shift-replacements', data),
  accept:         (id)                   => api.post(`/shift-replacements/${id}/accept`),
  decline:        (id, reason)           => api.post(`/shift-replacements/${id}/decline`, { reason: reason || null }),
  cancel:         (id, reason)           => api.post(`/shift-replacements/${id}/cancel`, { reason: reason || null }),
  inbox:          ()                     => api.get('/shift-replacements/inbox'),
  unreadCount:    ()                     => api.get('/shift-replacements/inbox/unread'),
  listForShift:   (shiftId)              => api.get(`/shift-replacements/shift/${shiftId}`),
  audit:          (id)                   => api.get(`/shift-replacements/${id}/audit`),
  expireStale:    ()                     => api.post('/shift-replacements/expire-stale'),
  stats:          ()                     => api.get('/shift-replacements/stats'),
  // Employee-initiated coverage requests (additive)
  employeeRequest:        (data)               => api.post('/shift-replacements/employee-request', data),
  employeeRequestsList:   (scope = 'mine')     => api.get(`/shift-replacements/employee-requests?scope=${scope}`),
  employeeRequestApprove: (id, data = {})      => api.post(`/shift-replacements/employee-requests/${id}/approve`, data),
  employeeRequestReject:  (id, data = {})      => api.post(`/shift-replacements/employee-requests/${id}/reject`, data),
};

// Canadian Payroll Phase 1 — country-gated at backend layer.
// All endpoints return 403 for non-CA tenants / employees.
export const canadianPayrollAPI = {
  eligible:            ()                          => api.get('/canadian-payroll/eligible'),
  earningsCategories:  ()                          => api.get('/canadian-payroll/earnings-categories'),
  deductionCategories: (province)                  => api.get('/canadian-payroll/deduction-categories', { params: province ? { province } : {} }),
  ytd:                 (employeeId, year)          => api.get(`/canadian-payroll/ytd/${employeeId}`, { params: year ? { year } : {} }),
  vacation:            (employeeId)                => api.get(`/canadian-payroll/vacation/${employeeId}`),
  updateVacation:      (employeeId, data)          => api.put(`/canadian-payroll/vacation/${employeeId}`, data),
  employerInfo:        ()                          => api.get('/canadian-payroll/employer-info'),
  updateEmployerInfo:  (data)                      => api.put('/canadian-payroll/employer-info', data),
  payslipPdfUrl:       (payslipId)                 => `${API_BASE}/canadian-payroll/payslip/${payslipId}/pdf`,
  downloadPayslipPdf:  (payslipId)                 => api.get(`/canadian-payroll/payslip/${payslipId}/pdf`, { responseType: 'blob' }),
};

// Lifetime Deal (super admin, foundation only)
export const lifetimeAPI = {
  // Marketplace sources
  listSources:   ()                  => api.get('/lifetime/sources'),
  createSource:  (data)              => api.post('/lifetime/sources', data),
  updateSource:  (id, data)          => api.patch(`/lifetime/sources/${id}`, data),
  deleteSource:  (id)                => api.delete(`/lifetime/sources/${id}`),
  // Codes
  listCodes:     (params = {})       => api.get('/lifetime/codes', { params }),
  generateCodes: (data)              => api.post('/lifetime/codes/generate', data),
  updateCodeStatus: (id, status)     => api.patch(`/lifetime/codes/${id}/status`, { status }),
  editCode:      (id, data)          => api.patch(`/lifetime/codes/${id}`, data),
  deleteCode:    (id)                => api.delete(`/lifetime/codes/${id}`),
  bulkCodes:     (operation, code_ids) => api.post('/lifetime/codes/bulk', { operation, code_ids }),
  importCodes:   (rows)              => api.post('/lifetime/codes/import', rows),
  exportCodesUrl: (params = {})      => {
    const qs = new URLSearchParams(params).toString();
    return `${API_BASE}/lifetime/codes/export${qs ? `?${qs}` : ''}`;
  },
  // Read-only
  customers:     ()                  => api.get('/lifetime/customers'),
  redemptions:   ()                  => api.get('/lifetime/redemptions'),
  stats:         ()                  => api.get('/lifetime/stats'),
  // Phase 6 — Stripe one-time $99 upgrade for LTD tier_1 / tier_2 tenants
  me:                     ()                  => api.get('/lifetime/me'),
  upgradeCheckout:        ()                  => api.post('/lifetime/upgrade/checkout', { origin_url: window.location.origin }),
  upgradeStatus:          (sessionId)         => api.get(`/lifetime/upgrade/status/${sessionId}`),
};

export default api;
